import React, { useState, useEffect, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import getAuth from "../../util/Auth";

// Create a context object
const AuthContext = React.createContext();

// Create a custom hook to use the context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Create a provider component
export const AuthProvider = ({ children }) => {
  const [isLogged, setIsLogged] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [user, setUser] = useState(null);

  // Check if user has admin role (roles 1-4)
  const checkIsAdmin = (userRole) => {
    return [1, 2, 3, 4].includes(Number(userRole));
  };

  // Update all auth states at once
  const updateAuthState = (userData) => {
    if (userData?.user_token) {
      setIsLogged(true);
      setUser(userData);
      setIsAdmin(checkIsAdmin(userData.user_role));
    } else {
      setIsLogged(false);
      setUser(null);
      setIsAdmin(false);
    }
  };

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    isLogged,
    isAdmin,
    user,
    setIsLogged,
    setIsAdmin,
    setUser,
    updateAuthState // Provide a method to update all states at once
  }), [isLogged, isAdmin, user]);

  useEffect(() => {
    const fetchAuthData = async () => {
      try {
        const response = await getAuth();
        updateAuthState(response);
      } catch (error) {
        console.error("Failed to fetch authentication data:", error);
        updateAuthState(null);
      }
    };

    fetchAuthData();
  }, []);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Define prop types for AuthProvider
AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};